/**
 * WorkingView — the right-hand panel of the Agent page.
 *
 * Default state: scrolling activity feed of what the workspace's agents
 * (and any automations they orchestrate) are doing, latest first.
 *
 * Tool/chat-driven state: when the agent calls a tool that requires user
 * approval, the corresponding tool component is rendered as a modal on
 * top of the activity feed with a blurred backdrop. The feed stays mounted
 * underneath so the user keeps the context they were just looking at.
 *
 * Future hook (not yet wired): an activity item can carry `needsApproval`
 * + a resource ref, and the card surfaces Approve / Reject buttons that
 * call back into the relevant pause-queue endpoint. Today no entries set
 * the flag — the buttons render only when an item declares them.
 */

import { useMemo, type FC, type ReactNode } from 'react';
import useSWR from 'swr';
import { CheckIcon, XCircleIcon, BoltIcon, MagnifyingGlassIcon, UserPlusIcon, EnvelopeIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';

const fetcher = (url: string) => fetch(url, { credentials: 'include' }).then(r => r.json());

export interface ActivityLogEntry {
  id: number | string;
  message: string;
  type: 'info' | 'success' | 'error' | 'search' | 'prospect' | 'email' | string;
  createdAt: string;
  /** Future fields — not on the server today, here so the UI compiles when added. */
  needsApproval?: boolean;
  resourceType?: string;
  resourceId?: string;
}

interface WorkingViewProps {
  /** When set, renders this content as a modal on top of the activity feed. */
  modal?: ReactNode;
}

export const WorkingView: FC<WorkingViewProps> = ({ modal }) => {
  // Workspace-wide activity. 5s poll keeps the feed fresh without being chatty.
  const { data, error, isValidating, isLoading } = useSWR<ActivityLogEntry[]>(
    '/api/agents/activity',
    fetcher,
    { refreshInterval: 5000 },
  );

  const entries = useMemo(() => Array.isArray(data) ? data : [], [data]);

  // Pill reflects the real connection state of the poll, not a hardcoded
  // animation. `live` = last fetch succeeded; `syncing` = a request is in
  // flight on the initial load; `offline` = last fetch errored.
  const status: 'live' | 'syncing' | 'offline' =
    error ? 'offline' : (isLoading || isValidating) && !data ? 'syncing' : 'live';
  const statusStyles =
    status === 'live'    ? { dot: 'bg-success-solid-alt animate-pulse', label: 'live activity' } :
    status === 'syncing' ? { dot: 'bg-warning-solid animate-pulse',   label: 'syncing activity' } :
                           { dot: 'bg-danger-solid',                    label: 'activity offline' };

  return (
    <div className="absolute inset-0 flex flex-col">
      {/* Soft fade — starts at ~80% opacity instead of full so cards stay
          faintly visible at the panel edges rather than being completely
          masked. */}
      <div className="absolute top-0 left-0 right-0 h-14 bg-gradient-to-b from-card/80 via-card/50 to-card/0 z-10 pointer-events-none" />
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-card/80 via-card/50 to-card/0 z-10 pointer-events-none" />

      <span className="absolute top-6 right-page-inset z-20 inline-flex items-center gap-1.5 text-[11px] text-muted-foreground pointer-events-none">
        <span className={`w-1.5 h-1.5 rounded-full ${statusStyles.dot}`} />
        {statusStyles.label}
      </span>

      {/* pt/pb match the gradient heights so the first item is fully visible
          just below the top fade and the last item slides under the bottom
          fade rather than slamming into the panel edge. */}
      <div className="flex-1 overflow-y-auto px-page-inset pt-14 pb-16 space-y-2">
        {error && (
          <ActivityCard
            tone="error"
            icon={<ExclamationTriangleIcon className="h-4 w-4" />}
            message="Couldn't load activity feed."
            timestamp={null}
          />
        )}
        {!error && entries.length === 0 && (
          <div className="h-full flex flex-col items-center justify-center text-center text-xs text-foreground px-6">
            <p className="font-medium mb-1">Nothing happening yet</p>
            <p>Activity from prospecting runs, email automations and agent actions will stream here.</p>
          </div>
        )}
        {entries.map(entry => (
          <ActivityCard
            key={entry.id}
            tone={toneForType(entry.type)}
            icon={iconForType(entry.type)}
            message={entry.message}
            timestamp={entry.createdAt}
            actions={entry.needsApproval ? (
              <ApprovalActions entry={entry} />
            ) : null}
          />
        ))}
      </div>

      {modal && (
        <div className="absolute inset-0 z-20 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-background/55 backdrop-blur-md" aria-hidden />
          <div className="relative z-10 w-full max-h-full overflow-y-auto rounded-2xl bg-card border border-border shadow-2xl">
            {modal}
          </div>
        </div>
      )}
    </div>
  );
};

export interface ActivityCardProps {
  tone: 'info' | 'success' | 'error' | 'search' | 'prospect' | 'email';
  icon: ReactNode;
  message: string;
  timestamp: string | null;
  actions?: ReactNode;
}

export const ActivityCard: FC<ActivityCardProps> = ({ tone, icon, message, timestamp, actions }) => {
  const accent = TONE_ACCENT[tone];
  return (
    <div className={`flex items-center gap-3 rounded-lg px-3.5 py-3 ${CARD_BG}`}>
      <div className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full ${accent.iconBg} ${accent.iconText}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <p className="text-[13px] leading-snug text-foreground flex-1">{message}</p>
          {timestamp && (
            <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums leading-snug pt-0.5">
              {formatRelative(timestamp)}
            </span>
          )}
        </div>
        {actions && <div className="mt-2 flex gap-1.5">{actions}</div>}
      </div>
    </div>
  );
};

const ApprovalActions: FC<{ entry: ActivityLogEntry }> = ({ entry }) => {
  // Wired stub — the real handler will POST to /api/.../approve|reject once
  // an upstream resource registers itself as gated. For now buttons just log.
  const handleApprove = () => {
    console.warn('approve activity', entry);
  };
  const handleReject = () => {
    console.warn('reject activity', entry);
  };
  return (
    <>
      <Button variant="primary" size="sm" className="h-7 text-[11px] px-3" onClick={handleApprove}>
        <CheckIcon className="h-3.5 w-3.5 mr-1" /> Approve
      </Button>
      <Button variant="secondary" size="sm" className="h-7 text-[11px] px-3" onClick={handleReject}>
        <XCircleIcon className="h-3.5 w-3.5 mr-1" /> Reject
      </Button>
    </>
  );
};

// Card chrome is uniform gray for all log types — keeps the feed calm. Only
// the icon bubble carries the type colour.
const CARD_BG = 'bg-surface-sunken';

const TONE_ACCENT: Record<ActivityCardProps['tone'], { iconBg: string; iconText: string }> = {
  info:     { iconBg: 'bg-surface-avatar',    iconText: 'text-content-body' },
  success:  { iconBg: 'bg-surface-avatar',    iconText: 'text-content-body' },
  error:    { iconBg: 'bg-surface-avatar',    iconText: 'text-content-body' },
  search:   { iconBg: 'bg-surface-avatar',    iconText: 'text-content-body' },
  prospect: { iconBg: 'bg-surface-avatar',    iconText: 'text-content-body' },
  email:    { iconBg: 'bg-surface-avatar',    iconText: 'text-content-body' },
};

export function toneForType(type: string): ActivityCardProps['tone'] {
  if (type === 'success' || type === 'error' || type === 'search' || type === 'prospect' || type === 'email') return type;
  return 'info';
}

export function iconForType(type: string): ReactNode {
  switch (type) {
    case 'success': return <CheckIcon className="h-4 w-4" />;
    case 'error': return <ExclamationTriangleIcon className="h-4 w-4" />;
    case 'search': return <MagnifyingGlassIcon className="h-4 w-4" />;
    case 'prospect': return <UserPlusIcon className="h-4 w-4" />;
    case 'email': return <EnvelopeIcon className="h-4 w-4" />;
    default: return <InformationCircleIcon className="h-4 w-4" />;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // hint to keep BoltIcon import when we add a new type
  void BoltIcon;
}

function formatRelative(iso: string): string {
  const ts = new Date(iso).getTime();
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
